/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType } from 'react';

import { jsx } from '@compiled/react';

import { type LinkItemResponse } from '@atlassian/navigation-bff-data-provider';
import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import type { SuggestionConfigContext } from '../../../../../types';
import { SuggestionItemBase, type SuggestionItemBaseProps } from '../../SuggestionItemBase';

import { useLinkItemConfig } from './hooks/use-link-item-config';

export interface LinkItemProps extends SuggestionItemBaseProps {
	context: SuggestionConfigContext;
	product: UniversalCreateSourceProduct;
	responseItem: LinkItemResponse; // override the defaul type
}

/**
 * Higher-order component that wraps a component with link item configuration
 * @param WrappedComponent - The component to wrap with link item configuration
 * @returns A new component with link item configuration applied
 */
function withLinkItemConfig<P extends LinkItemProps>(
	WrappedComponent: ComponentType<P>,
): ComponentType<LinkItemProps> {
	return function WithLinkItemConfigComponent(props: LinkItemProps) {
		const linkItemConfig = useLinkItemConfig(props.responseItem);

		if (!linkItemConfig.href) {
			return null; // early return should now show the item when computed href is invalid
		}

		return <WrappedComponent {...(props as P)} customisedItemConfig={linkItemConfig} />;
	};
}

export const LinkItem = withLinkItemConfig(SuggestionItemBase);
